/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useRef, useState} from 'react'
import {useLocation} from 'react-router-dom'

// Components
import {Box} from '@salesforce/retail-react-app/app/components/shared/ui'

// Project Components
import Seo from '../../components/seo'
import PDComponent from '../../components/page-designer/pd-component'

//Hooks
import useEinstein from '@salesforce/retail-react-app/app/hooks/use-einstein'
import {useCustomerId, useCustomerOrders, useCustomerType} from '@salesforce/commerce-sdk-react'

// Constants
import {MAX_CACHE_AGE, NOINDEX_PAGE_IDs, STALE_WHILE_REVALIDATE} from '../../constants'

import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {useParams} from 'react-router-dom/cjs/react-router-dom.min'
import LoadingSpinner from '../../components/loading-spinner'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {Helmet} from 'react-helmet'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useSearchParams} from '@salesforce/retail-react-app/app/hooks'
import {onClient} from '@salesforce/commerce-sdk-react/utils'
import useMonetate from '../../hooks/use-monetate'
import useTagManager from '../../hooks/use-tag-manager'
import {useWishList} from '../../hooks/use-wish-list'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {CookieStorage} from '@salesforce/commerce-sdk-react/auth/storage'
import {getTotalQuantity} from '../../utils/basket-utils'
import {useGlobaleContext} from '../../contexts/globale'
import usePageDesigner from '../../hooks/use-page-designer'
import EspSkeleton from '../home/partials/skeleton'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import useCustomerGroups from '../../hooks/use-customer-groups'

/**
 * This is the home page for Retail React App.
 * The page is created for demonstration purposes.
 * The page renders SEO metadata and a few promotion
 * categories and products, data is from local file.
 */
const ContentPage = () => {
    const einstein = useEinstein()
    const {pathname} = useLocation()
    const tagManager = useTagManager()
    const monetate = useMonetate()
    const {data: customerGroupIds} = useCustomerGroups()
    const {countryISO, currencyCode} = useGlobaleContext()
    const debounceRef = useRef(null)
    const {isRegistered, customerType} = useCustomerType()
    const {data: customer} = useCurrentCustomer()
    const searchParams = useSearchParams({limit: 1, offset: 0})
    const {data: basket, isLoading: isBasketLoading} = useCurrentBasket()
    const {limit, offset} = searchParams[0]
    const {data: wishlist, isLoading: isWishlistLoading} = useWishList()
    const customerId = useCustomerId()
    const [globalDataLoaded, setGlobalDataLayerLoaded] = useState(false)
    const {data: {data: orders} = {}, isLoading: isOrdersLoading} = useCustomerOrders(
        {
            parameters: {customerId, limit, offset}
        },
        {enabled: onClient() && !!customerId && customer?.customerType === 'registered'}
    )

    const {
        app: {commerceAPI}
    } = getConfig()
    const {changeUp} = commerceAPI
    const {configChangeup} = changeUp || {}
    var changeUpProductID = null

    try {
        const jsonConfig = JSON.parse(configChangeup)
        changeUpProductID = jsonConfig.enabled ? jsonConfig.productID : null
    } catch (e) {
        console.log(e.message)
    }
    const totalQuantity = getTotalQuantity(basket, changeUpProductID)

    const cookie =
        onClient() &&
        new CookieStorage({
            keySuffix: '',
            sharedContext: !onClient()
        })

    const navigate = useNavigation()

    useEffect(() => {
        const city = cookie.get('city')
        const country = cookie.get('countryCode') || 'US'
        const state = cookie.get('state')
        if (!country) return

        if (
            ((customer?.customerType === 'registered' && customer?.email) ||
                customer?.customerType === 'guest') &&
            !isBasketLoading &&
            !globalDataLoaded
        ) {
            if (debounceRef.current) clearTimeout(debounceRef.current)

            debounceRef.current = setTimeout(() => {
                tagManager.globalDataLoad(
                    'landing page',
                    'landing page',
                    totalQuantity,
                    basket?.productTotal,
                    customer,
                    city,
                    country,
                    state
                )
                setGlobalDataLayerLoaded(true)
            }, 2000)
        }
    }, [customer?.customerType, customer?.email, isBasketLoading, cookie])

    useEffect(() => {
        if (
            !isBasketLoading &&
            (!isOrdersLoading || customer?.customerType !== 'registered') &&
            !!customerType &&
            (!isWishlistLoading || customer?.customerType !== 'registered')
        ) {
            window.lastOrderDate = orders?.[0]?.creationDate || 'n/a'
            window.mtBreadcrumb = ['Home', pageTitle]
            monetate.monetateTrackPage(
                'main',
                ['Landing Page', pageTitle],
                basket?.productItems || [],
                customer?.customerType === 'registered',
                wishlist?.customerProductListItems?.length || 0,
                null,
                null,
                null,
                basket?.giftCertificateItems
            )
            window.monetateRef = 'home'
        }
    }, [isBasketLoading, isOrdersLoading, customerType, isWishlistLoading])
    useEffect(() => {
        setGlobalDataLayerLoaded(false)
    }, [pathname])
    useEffect(() => {
        // 'CookieStorage' takes some miliseconds to fetch cookies, that's why we've used 'useEffect'
        const city = cookie.get('city')
        const country = cookie.get('countryCode') || 'US'
        const state = cookie.get('state')
        if (
            globalDataLoaded &&
            ((customer?.customerType === 'registered' && customer?.email) ||
                customer?.customerType === 'guest')
        ) {
            try {
                tagManager.dataLayerLoad(
                    'other resources',
                    isRegistered,
                    null,
                    countryISO,
                    currencyCode,
                    customer,
                    city,
                    country,
                    state,
                    null,
                    null,
                    'landing page'
                )
            } catch (e) {
                tagManager.dataLayerLoad(
                    'Home',
                    isRegistered,
                    'Home',
                    null,
                    null,
                    customer,
                    city,
                    country,
                    state,
                    null,
                    null,
                    'home'
                )
            }
        }
    }, [globalDataLoaded, customer?.customerType, customer?.email])

    // useServerContext is a special hook introduced in v3 PWA Kit SDK.
    // It replaces the legacy `getProps` and provide a react hook interface for SSR.
    // it returns the request and response objects on the server side,
    // and these objects are undefined on the client side.
    const {res} = useServerContext()
    if (res) {
        res.set(
            'Cache-Control',
            `s-maxage=${MAX_CACHE_AGE}, stale-while-revalidate=${STALE_WHILE_REVALIDATE}`
        )
    }

    const {pageId} = useParams()
    const location = useLocation()
    const {data: page, isLoading} = usePageDesigner(
        {pageId: pageId, customerGroupIds: customerGroupIds},
        {enabled: true}
    )
    const {pageTitle, pageDescription, id} = page || {}

    // check if page designer is offline and redirect to page not found(404)
    if (page && !('regions' in page)) {
        navigate('/page-not-found')
    }

    /**************** Einstein ****************/
    useEffect(() => {
        einstein.sendViewPage(pathname)
    }, [])

    useEffect(() => {
        if (isLoading) return

        const hash = window.location.hash
        if (!hash) return

        const el = document.getElementById(hash.slice(1))
        if (!el) return

        el.scrollIntoView({behavior: 'smooth'})
    }, [pageId, isLoading, location?.hash])

    const {hostUrl} = commerceAPI.storefront
    return (
        <Box data-testid={id} className="content-page">
            <Helmet>
                <link rel="canonical" href={hostUrl + pathname}></link>
            </Helmet>
            <Seo
                title={pageTitle}
                description={pageDescription}
                noIndex={NOINDEX_PAGE_IDs?.indexOf(pageId) !== -1}
            />
            {isLoading && !page && <EspSkeleton />}
            {isLoading && <LoadingSpinner />}
            {!isLoading && page && <PDComponent page={page} parent={true} />}
        </Box>
    )
}

ContentPage.getTemplateName = () => 'content-page'

export default ContentPage
